import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { safeStorage } from '../utils/safeStorage';
import { isMasterEmail } from '../constants';

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, status } = useAuth();
  const location = useLocation();
  const [checkingTriaging, setCheckingTriaging] = useState(true);
  const [needsTriaging, setNeedsTriaging] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function checkTriagingStatus() {
      // Se não autenticado, não bloqueia (Modo Visitante / Demo gerenciado pelo AppShell)
      if (status !== 'authenticated' || !session?.user?.id) {
        if (isMounted) {
          setCheckingTriaging(false);
          setNeedsTriaging(false);
        }
        return;
      }

      // Usuários Master nunca passam por triagem
      if (isMasterEmail(session.user.email)) {
        sessionStorage.setItem(`rmax_triaging_completed_${session.user.id}`, 'true');
        if (isMounted) {
          setCheckingTriaging(false);
          setNeedsTriaging(false);
        }
        return;
      }

      // 1. Verifica cache rápido de sessão para evitar overhead de rede contínuo
      const cachedTriaging = sessionStorage.getItem(`rmax_triaging_completed_${session.user.id}`);
      if (cachedTriaging === 'true') {
        if (isMounted) {
          setCheckingTriaging(false);
          setNeedsTriaging(false);
        }
        return;
      }

      // 2. Verifica se há contexto pendente de triagem escolhido antes do login (/selecionar-perfil ou LPs)
      const pendingNicheName = safeStorage.getItem('rmax_pending_niche_name');
      const pendingVertical = safeStorage.getItem('rmax_pending_vertical');
      const pendingPlan = safeStorage.getItem('rmax_pending_plan');
      const referredCoupon = safeStorage.getItem('rmax_referred_coupon');

      if (pendingNicheName || pendingVertical || pendingPlan) {
        try {
          const tier = pendingPlan || 'empresa';
          const vertical = pendingVertical || null;
          const nicheName = pendingNicheName || (pendingVertical === 'pet_vet' ? 'Veterinária & Pet Shop' : 'Geral');

          const now = new Date();
          const trialEndsAt = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString();
          const planNameMap: Record<string, 'Grátis' | 'Pro' | 'Especialista' | 'Empresa'> = {
            gratis: 'Grátis',
            pro: 'Pro',
            especialista: 'Especialista',
            empresa: 'Empresa'
          };

          try {
            await supabase.rpc('complete_profile_triaging', {
              p_tier: tier,
              p_vertical: vertical,
              p_coupon: referredCoupon || null
            });
          } catch (rpcErr) {
            console.warn('RPC complete_profile_triaging fallback em ProtectedRoute:', rpcErr);
          }

          // Atualizações atômicas de fallback para garantir consistência no banco
          await supabase
            .from('users')
            .update({
              plan: planNameMap[tier] || 'Grátis',
              niche: nicheName,
              selected_tier: tier,
              selected_vertical: vertical,
              onboarding_completed: true,
              onboarding_completed_at: now.toISOString(),
              max_free_quotes: tier === 'gratis' ? 5 : 999999
            })
            .eq('id', session.user.id);

          await supabase
            .from('subscriptions')
            .upsert({
              user_id: session.user.id,
              status: tier === 'gratis' ? 'active' : 'trialing',
              plan_type: tier,
              trial_starts_at: tier === 'gratis' ? null : now.toISOString(),
              trial_ends_at: tier === 'gratis' ? null : trialEndsAt,
              access_ends_at: tier === 'gratis' ? null : trialEndsAt,
              trial_used: tier !== 'gratis'
            }, { onConflict: 'user_id' });

          await supabase
            .from('user_business_profiles')
            .upsert({
              user_id: session.user.id,
              subscription_plan_type: tier,
              niche: nicheName
            }, { onConflict: 'user_id' });

          sessionStorage.setItem(`rmax_triaging_completed_${session.user.id}`, 'true');
          safeStorage.removeItem('rmax_pending_vertical');
          safeStorage.removeItem('rmax_pending_plan');
          safeStorage.removeItem('rmax_pending_niche_id');
          safeStorage.removeItem('rmax_pending_niche_name');
          safeStorage.removeItem('rmax_pending_redirect');

          if (isMounted) {
            setCheckingTriaging(false);
            setNeedsTriaging(false);
          }
          return;
        } catch (fastTrackErr) {
          console.warn('Erro ao processar triagem pendente em ProtectedRoute:', fastTrackErr);
        }
      }

      // 3. Consulta no banco de dados se o usuário já concluiu triagem / selecionou nicho
      try {
        const { data, error } = await supabase
          .from('users')
          .select('id, plan, onboarding_completed, selected_vertical, selected_tier, niche, business_name')
          .eq('id', session.user.id)
          .maybeSingle();

        if (!isMounted) return;

        if (!error && data) {
          const hasSelectedVertical = Boolean(data.selected_vertical) && data.onboarding_completed === true;
          const hasKnownNicheOrBusiness =
            Boolean(data.niche && data.niche.trim() !== '' && data.niche !== 'Geral') ||
            Boolean(data.business_name && data.business_name.trim().length > 2);
          const hasConfiguredProfile = hasSelectedVertical || hasKnownNicheOrBusiness || data.onboarding_completed === true;

          if (hasConfiguredProfile) {
            sessionStorage.setItem(`rmax_triaging_completed_${session.user.id}`, 'true');
            if (pendingPlan) safeStorage.removeItem('rmax_pending_plan');
            if (referredCoupon) safeStorage.removeItem('rmax_referred_coupon');
            setNeedsTriaging(false);
          } else {
            // Conta nova sem nicho configurado: direciona obrigatoriamente para seleção de perfil
            setNeedsTriaging(true);
          }
        } else if (!error && !data) {
          setNeedsTriaging(true);
        }
      } catch (e) {
        console.warn('Erro ao verificar triagem do usuário em ProtectedRoute:', e);
      } finally {
        if (isMounted) setCheckingTriaging(false);
      }
    }

    checkTriagingStatus();

    return () => {
      isMounted = false;
    };
  }, [session, status]);

  if (status === 'loading' || (status === 'authenticated' && checkingTriaging)) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-4" />
        <h2 className="text-base font-bold text-slate-900 mb-1 font-sans">Abrindo seu ambiente seguro</h2>
        <p className="text-slate-500 text-xs font-sans">Preparando seu workspace...</p>
      </div>
    );
  }

  // Intercepta e redireciona para triagem caso o perfil ainda não tenha sido escolhido
  if (status === 'authenticated' && needsTriaging && location.pathname !== '/selecionar-perfil') {
    return <Navigate to="/selecionar-perfil" replace />;
  }

  return <>{children}</>;
}
